import React, { memo, useMemo } from 'react'
import { Card, Button, Input, Table, Spin, Avatar, Select } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import moment from 'moment'
import './CreditUsageTable.scss'

const { Search } = Input
const { Option } = Select

const CreditUsageTable = memo(({
  title = 'Credit Usage Report',
  data,
  loading,
  onExportClick,
  onSearch,
  searchTerm,
  pagination,
  onPageChange,
  activeTypes = [],
  selectedType,
  onTypeChange
}) => {
  const tableData = useMemo(() => {
    if (!data) return []

    return data.map((item, index) => ({
      key: item.course_id || index,
      courseName: item.title || 'Unknown Course',
      totalPages: item.total_pages || 0,
      maxPictures: item.max_picture_allowed || 0,
      actualPhotos: item.actual_photos || 0,
      extraPhotos: item.extra_photos || 0,
      extraCostPhotos: item.extra_cost_for_photos || 0,
      maxText: (item.total_pages || 0) * 3,
      actualText: item.actual_texts || 0,
      extraText: item.extra_texts || 0,
      extraCostText: item.extra_cost_for_texts || 0,
      maxVoiceSecs: (item.total_pages || 0) * 60,
      actualVoiceSecs: item.actual_voices || 0,
      extraVoiceSecs: item.extra_voices || 0,
      extraCostVoice: item.extra_cost_for_voices || 0,
      basicCostToken: item.total_basic_cost_token || 0,
      totalCostTokens: item.total_tokens_cost || 0,
      creatorName: item.user ? `${item.user.first_name || ''} ${item.user.last_name || ''}`.trim() : 'Unknown',
      creatorAvatar: item.user?.avatar?.link || ''
    }))
  }, [data])

  const columns = useMemo(() => {
    return [
      {
        title: 'Creator',
        dataIndex: 'creatorName',
        key: 'creatorName',
        width: 200,
        render: (name, record) => (
          <div className="user-info" style={{ display: 'flex', alignItems: 'center' }}>
            <Avatar src={record.creatorAvatar} className="user-avatar" style={{ marginRight: '8px' }}>
              {name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <span className="user-name" style={{ fontWeight: 500 }}>{name}</span>
          </div>
        )
      },
      {
        title: 'Course Name',
        dataIndex: 'courseName',
        key: 'courseName',
        width: 150,
      },
      {
        title: 'Total Pages',
        dataIndex: 'totalPages',
        key: 'totalPages',
        width: 100,
      },
      {
        title: 'Max Pictures (Pages x 2)',
        dataIndex: 'maxPictures',
        key: 'maxPictures',
        width: 150,
      },
      {
        title: 'Actual Photos',
        dataIndex: 'actualPhotos',
        key: 'actualPhotos',
        width: 120,
      },
      {
        title: 'Extra Photos',
        dataIndex: 'extraPhotos',
        key: 'extraPhotos',
        width: 120,
      },
      {
        title: 'Extra Cost (Photos)',
        dataIndex: 'extraCostPhotos',
        key: 'extraCostPhotos',
        width: 120,
        render: (val) => Number(val).toLocaleString(undefined, { maximumFractionDigits: 6 })
      },
      {
        title: 'Max Text (Pages x 3)',
        dataIndex: 'maxText',
        key: 'maxText',
        width: 150,
      },
      {
        title: 'Actual Text',
        dataIndex: 'actualText',
        key: 'actualText',
        width: 100,
      },
      {
        title: 'Extra Text',
        dataIndex: 'extraText',
        key: 'extraText',
        width: 100,
      },
      {
        title: 'Extra Cost (Text)',
        dataIndex: 'extraCostText',
        key: 'extraCostText',
        width: 120,
        render: (val) => Number(val).toLocaleString(undefined, { maximumFractionDigits: 6 })
      },
      {
        title: 'Max Voice Secs (Pages x 60)',
        dataIndex: 'maxVoiceSecs',
        key: 'maxVoiceSecs',
        width: 150,
      },
      {
        title: 'Actual Voice (Sec)',
        dataIndex: 'actualVoiceSecs',
        key: 'actualVoiceSecs',
        width: 120,
      },
      {
        title: 'Extra Voice (Sec)',
        dataIndex: 'extraVoiceSecs',
        key: 'extraVoiceSecs',
        width: 120,
      },
      {
        title: 'Extra Cost (Voice)',
        dataIndex: 'extraCostVoice',
        key: 'extraCostVoice',
        width: 120,
        render: (val) => Number(val).toLocaleString(undefined, { maximumFractionDigits: 6 })
      },
      {
        title: 'Basic Cost Token (Pages x 60)',
        dataIndex: 'basicCostToken',
        key: 'basicCostToken',
        width: 150,
        render: (val) => Number(val).toLocaleString(undefined, { maximumFractionDigits: 6 })
      },
      {
        title: 'Total Cost Tokens',
        dataIndex: 'totalCostTokens',
        key: 'totalCostTokens',
        width: 120,
        render: (val) => Number(val).toLocaleString(undefined, { maximumFractionDigits: 6 })
      }
    ]
  }, [])

  const handleExport = () => {
    if (onExportClick) {
      onExportClick(tableData)
    } else {
      if (!tableData || tableData.length === 0) return

      const header = columns.map(c => c.title)
      const rows = tableData.map((row) => {
        return columns.map(c => row[c.dataIndex])
      })

      const csvContent = [header, ...rows]
        .map(e => e.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
        .join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const fileName = `credit_usage_${moment().format('YYYY-MM-DD')}.csv`

      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }

  return (
    <Card className="course-credit-usage-card" style={{ marginTop: '24px' }}>
      <div className="course-credit-usage-content">
        <div className="section-header" style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="section-title" style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{title}</h3>
          {activeTypes.length > 0 && (
            <div className="type-filter-container">
              <span className="type-label">Type: </span>
              <Select
                value={selectedType}
                onChange={onTypeChange}
                className="type-select"
                placeholder="All Services"
                style={{ width: 150 }}
              >
                {activeTypes.map(type => {
                  const colorMap = {
                    'ai-image': '#5B6EE2',
                    'ai_video': '#C9D3FA',
                    audio: '#3875F6',
                    course: '#44959A',
                    podcast: '#EB3B88',
                    maze: '#9FD4FB',
                    sharebot: '#2DCCB9'
                  }

                  let displayName = type.charAt(0).toUpperCase() + type.slice(1)
                  if (type === 'ai-image') displayName = 'AI-Image'
                  else if (type === 'ai_video') displayName = 'Ai-Video'

                  return (
                    <Option key={type} value={type}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colorMap[type] || '#888', display: 'inline-block' }} />
                        {displayName}
                      </div>
                    </Option>
                  )
                })}
              </Select>
            </div>
          )}
        </div>

        <div className="filters-section" style={{ marginBottom: '16px' }}>
          <div className="header-actions" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px' }}>
            <Button
              onClick={handleExport}
              disabled={tableData.length === 0}
            >
              <DownloadOutlined /> Export Report
            </Button>
            <Search
              placeholder="Search by title, name..."
              allowClear
              value={searchTerm}
              onChange={e => onSearch && onSearch(e.target.value)}
              onSearch={value => onSearch && onSearch(value)}
              style={{ width: 250 }}
            />
          </div>
        </div>

        <div className="table-section">
          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={tableData}
              pagination={{
                ...pagination,
                onChange: onPageChange,
                onShowSizeChange: onPageChange,
                showSizeChanger: false,
                pageSizeOptions: [10, 20, 50, 100]
              }}
              className="course-credit-table"
              scroll={{ x: 'max-content' }}
            />
          </Spin>
        </div>
      </div>
    </Card>
  )
})

export default CreditUsageTable
